import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { transcriptionAPI } from '../services/api';
import { VERSION_CONTROL_API_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// Import components
import TranscriptionHeader from '../components/transcription/TranscriptionHeader';
// Transcript panel is hidden for now; restore this import with the block below.
// import TranscriptionContent from '../components/transcription/TranscriptionContent';
import VersionHistory from '../components/transcription/VersionHistory';
import SummaryVersionHistory from '../components/transcription/SummaryVersionHistory';
import SummarySection from '../components/transcription/SummarySection';
import VersionModal from '../components/transcription/VersionModal';
import SummaryVersionModal from '../components/transcription/SummaryVersionModal';
import JobMetadataPanel from '../components/transcription/JobMetadataPanel';
import DraftBanner from '../components/transcription/DraftBanner';
import AudioReviewPanel from '../components/transcription/AudioReviewPanel';
import CertifyModal from '../components/transcription/CertifyModal';
import LanguageVersionTabs from '../components/transcription/LanguageVersionTabs';
import RegenerateModal from '../components/transcription/RegenerateModal';
import AudioAssetPanel from '../components/transcription/AudioAssetPanel';
import RegenerationAuditPanel from '../components/transcription/RegenerationAuditPanel';
import { DISPLAY_ASR_MODEL, parseTranscriptionRecord } from '../utils/transcriptionMeta';
import usePermissions from '../hooks/usePermissions';
import useAudioSync from '../hooks/useAudioSync';
import useLanguageOptions from '../hooks/useLanguageOptions';
import { DEFAULT_LANGUAGES } from '../config/languages';
import { getLanguageLabel } from '../utils/languages';
import {
  getExistingLanguageLabels,
  hasPendingLanguageVersions,
} from '../utils/regeneration';
import { isProcessingStatus } from '../utils/transcriptionStatus';
import {
  buildLabelMapFromSegments,
  normalizeSpeakerSegments,
} from '../utils/speakerSegments';
import SpeakerRenameDialog from '../components/transcription/SpeakerRenameDialog';
import PageLoadingState from '../components/layout/PageLoadingState';
import {
  isTranscriptCertified,
  prependDraftNotice,
  TRANSCRIPT_STATUS,
} from '../utils/transcriptCertification';

// Import utilities
import {
  calculateWordCount,
  parseDurationToSeconds,
  formatDuration,
  formatDate,
  extractSummaryFromRecord,
  formatSummary,
  processVersionContent
} from '../components/transcription/transcriptionUtils';

import {
  downloadAsDocx,
  downloadAsPdf,
  downloadSummaryDocx,
  downloadSummaryPdf,
  downloadVersionAsPDF,
  downloadVersionAsDOCX,
  downloadSummaryVersionAsPDF,
  downloadSummaryVersionAsDOCX,
} from '../components/transcription/downloadUtils';
import {
  buildCourtTranscriptDocument,
  downloadCourtTranscriptPdf,
} from '../components/transcription/courtTranscriptPdf';

// Cache busting - force reload
// console.log('🔄 CACHE BUST: TranscriptionDetail.js loaded at', new Date().toISOString());

const TranscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canCertify, canRequestRegeneration, role, displayRole } = usePermissions();
  const { restrictedLanguages } = useLanguageOptions();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [savingVersion, setSavingVersion] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const [userName, setUserName] = useState(user ? `${user.first_name}` : 'Current User');
  const [summary, setSummary] = useState(null);
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isVersionsDropdownOpen, setIsVersionsDropdownOpen] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [summaryVersions, setSummaryVersions] = useState([]);
  const [summaryVersionsLoading, setSummaryVersionsLoading] = useState(false);
  const [showSummaryVersions, setShowSummaryVersions] = useState(false);
  const [selectedSummaryVersion, setSelectedSummaryVersion] = useState(null);
  const [savingSummaryVersion, setSavingSummaryVersion] = useState(false);
  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [certifyError, setCertifyError] = useState('');
  const [speakerSegments, setSpeakerSegments] = useState([]);
  const [speakerLabelMap, setSpeakerLabelMap] = useState({});
  const [renameTarget, setRenameTarget] = useState(null);
  const [savingSpeakerLabel, setSavingSpeakerLabel] = useState(false);
  const [speakerLabelError, setSpeakerLabelError] = useState('');
  const [languageVersions, setLanguageVersions] = useState([]);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [targetLanguages, setTargetLanguages] = useState(null);
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [regenError, setRegenError] = useState('');

  const [transcription, setTranscription] = useState({
    id: id,
    title: '',
    content: '',
    date: '',
    duration: '',
    status: '',
    wordCount: 0,
    language: 'English',
    case_reference: '',
    case_id: null,
    model_version: DISPLAY_ASR_MODEL,
    queued_at: null,
    completed_at: null,
    failure_reason: '',
    jobStatus: '',
    source_type: '',
    transcript_status: TRANSCRIPT_STATUS.DRAFT,
    audio_url: null,
    duration_seconds: 0,
    segments: null,
    certified_at: null,
    certified_by: '',
    certifying_role: '',
    is_regenerated: false,
    audio_asset_id: null,
    audio_asset: null,
    session_deleted: false,
    parent_transcription_id: null,
    regeneration_reason: '',
    regeneration_reason_label: '',
    regeneration_requested_by: '',
    regenerated_at: null,
  });
  const [editedContent, setEditedContent] = useState('');

  const isLocked = isTranscriptCertified(transcription);
  const isCertified = isLocked;

  const audioSync = useAudioSync({
    content: transcription.content,
    durationSeconds: transcription.duration_seconds || 0,
    apiSegments: speakerSegments.length > 0 ? speakerSegments : transcription.segments,
    audioUrl: transcription.audio_url,
  });

  const aiDraftVersionNumber = useMemo(() => {
    const aiDraft = versions.find((v) => v.change_type === 'ai_draft');
    if (aiDraft) return aiDraft.version_number;
    if (versions.length > 0) {
      return Math.min(...versions.map((v) => v.version_number));
    }
    return null;
  }, [versions]);

  const existingLanguageLabels = useMemo(
    () => getExistingLanguageLabels(languageVersions),
    [languageVersions]
  );

  const showRegenerateButton = useMemo(() => {
    if (transcription.session_deleted) return false;
    const jobStatus = transcription.jobStatus || transcription.status;
    const ready = ['complete', 'completed'].includes(String(jobStatus).toLowerCase());
    if (!ready || !transcription.content) return false;
    const takenCount = existingLanguageLabels.size;
    return takenCount < DEFAULT_LANGUAGES.length;
  }, [transcription.session_deleted, transcription.jobStatus, transcription.status, transcription.content, existingLanguageLabels]);

  const isRestrictedLanguage = useMemo(() => {
    const lang = getLanguageLabel(transcription.language).toLowerCase();
    return restrictedLanguages.some((r) => r.toLowerCase() === lang);
  }, [transcription.language, restrictedLanguages]);

  const isJobProcessing = useMemo(() => {
    const currentVersion = (languageVersions || []).find((v) => String(v.id) === String(id));
    const status = currentVersion?.status || transcription.jobStatus || transcription.status;
    if (isProcessingStatus(status)) return true;
    // Empty regenerated/queued views sometimes arrive marked complete before body text exists.
    const hasContent = Boolean(transcription.content);
    if (!hasContent && String(status).toLowerCase() !== 'failed') {
      return Boolean(transcription.is_regenerated || transcription.parent_transcription_id);
    }
    return false;
  }, [
    languageVersions,
    id,
    transcription.jobStatus,
    transcription.status,
    transcription.content,
    transcription.is_regenerated,
    transcription.parent_transcription_id,
  ]);

  const applyParsedTranscription = (data, parsedDurationOverride) => {
    const parsed = parseTranscriptionRecord(data);
    const parsedDuration =
      parsedDurationOverride ?? parseDurationToSeconds(getRawDurationFromData(data));

    const transcriptionData = {
      ...parsed,
      content: parsed.transcribed_content,
      date: formatDate(parsed.created_at),
      duration: formatDuration(parsedDuration),
      wordCount: calculateWordCount(parsed.transcribed_content),
    };

    setTranscription(transcriptionData);
    setEditedContent(transcriptionData.content);
    setOriginalContent(transcriptionData.content);
    setLanguageVersions(parsed.language_versions || []);

    const normalizedSegments = normalizeSpeakerSegments(parsed.segments || data.segments);
    setSpeakerSegments(normalizedSegments);
    if (!normalizedSegments.length && id) {
      transcriptionAPI.getAudioOffsets(id).then((resp) => {
        const offsets = resp.data?.data?.offsets || [];
        if (!offsets.length) return;
        setSpeakerSegments(normalizeSpeakerSegments(offsets));
      }).catch(() => {});
    }
    setSpeakerLabelMap({
      ...buildLabelMapFromSegments(normalizedSegments),
      ...(data.speaker_label_map || {}),
    });

    return transcriptionData;
  };

  const getRawDurationFromData = (data) => {
    const allDurationFields = {
      duration: data.duration,
      audio_duration: data.audio_duration,
      length: data.length,
      duration_seconds: data.duration_seconds,
      audio_length: data.audio_length,
      audio_length_seconds: data.audio_length_seconds,
      metadata: data.metadata,
      metadata_duration: data.metadata?.duration,
      metadata_duration_seconds: data.metadata?.duration_seconds,
    };
    return (
      Object.values(allDurationFields).find(
        (val) => val !== null && val !== undefined && val !== 0 && val !== '' && val !== '0'
      ) || 0
    );
  };

  // Toast UI Editor configuration is handled in the ToastEditor component
  
  // Update userName when user data changes
  useEffect(() => {
    if (user && user.first_name) {
      setUserName(`${user.first_name}`);
    }
  }, [user]);

  useEffect(() => {
    if (showVersions) {
      setTimeout(() => {
        document.getElementById('version-history-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showVersions]);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        document.getElementById('transcription-content-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isEditing]);

  useEffect(() => {
    if (showSummaryVersions) {
      setTimeout(() => {
        document.getElementById('summary-version-history-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showSummaryVersions]);

  // Helper function to normalize content for comparison
  const normalizeContent = (content) => {
    if (!content) return '';
    
    // Since we're now using Markdown, we can compare directly
    // Normalize whitespace for fair comparison
    let normalized = content
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .replace(/\n\s*\n/g, '\n') // Replace multiple newlines with single newline
      .trim();
    
    return normalized;
  };

  // Helper function to compare content and determine if changes were made
  const hasContentChanged = (originalContent, editedContent) => {
    if (!originalContent && !editedContent) return false;
    if (!originalContent || !editedContent) return true;
    
    // Normalize both contents for fair comparison
    const originalText = normalizeContent(originalContent);
    const editedText = normalizeContent(editedContent);
    
    // console.log('Content comparison:', {
    //   original: originalText.substring(0, 100) + '...',
    //   edited: editedText.substring(0, 100) + '...',
    //   changed: originalText !== editedText
    // });
    
    return originalText !== editedText;
  };

  // Fetch versions from API
  const fetchVersions = async () => {
    try {
      setVersionsLoading(true);
      const response = await fetch(`${VERSION_CONTROL_API_URL}/transcriptions/original/${id}/versions`);
      const data = await response.json();
      
      if (data.success) {
        setVersions(data.data);
      } else {
        // console.error('Failed to fetch versions:', data.error);
      }
    } catch (error) {
      // console.error('Error fetching versions:', error);
    } finally {
      setVersionsLoading(false);
    }
  };

  // Fetch summary versions from API
  const fetchSummaryVersions = async () => {
    try {
      setSummaryVersionsLoading(true);
      const response = await fetch(`${VERSION_CONTROL_API_URL}/transcriptions/original/${id}/summary-versions`);
      const data = await response.json();

      if (data.success) {
        setSummaryVersions(data.data);
      } else {
        // console.error('Failed to fetch summary versions:', data.error);
      }
    } catch (error) {
      // console.error('Error fetching summary versions:', error);
    } finally {
      setSummaryVersionsLoading(false);
    }
  };

  // Create new version
  const createNewVersion = async (content, changeType = 'edit', changeSummary = '') => {
    try {
      setSavingVersion(true);
      const response = await fetch(`${VERSION_CONTROL_API_URL}/transcriptions/original/${id}/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content,
          change_type: changeType,
          change_summary: changeSummary,
          created_by_name: userName,
          title: transcription.title
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh versions list
        await fetchVersions();
        return data.data;
      } else {
        throw new Error(data.error || 'Failed to create version');
      }
    } catch (error) {
      // console.error('Error creating version:', error);
      throw error;
    } finally {
      setSavingVersion(false);
    }
  };

  // Restore to previous version
  const restoreVersion = async (versionNumber) => {
    if (isLocked) {
      alert('Certified transcripts cannot be restored to a previous version.');
      return;
    }
    try {
      setSavingVersion(true);
      
      // Find the version to restore
      const versionToRestore = versions.find(v => v.version_number === versionNumber);
      if (!versionToRestore) {
        alert('Version not found');
        return;
      }
      
      // Create a new version with the restored content
      const changeSummary = `Restored to version ${versionNumber} by ${userName}`;
      const success = await createNewVersion(versionToRestore.content, 'restore', changeSummary);
      
      if (success) {
        // Update the main transcription content
        setTranscription({ ...transcription, content: versionToRestore.content });
        setEditedContent(versionToRestore.content);
        setIsEditing(false);
        setShowOriginal(false);
        
        // Refresh versions list
        await fetchVersions();
        
        alert(`Successfully restored to version ${versionNumber}`);
      } else {
        alert('Failed to restore version. Please try again.');
      }
    } catch (error) {
      // console.error('Error restoring version:', error);
      alert('Failed to restore version. Please try again.');
    } finally {
      setSavingVersion(false);
    }
  };
  const handleCertify = async (correctionSummary) => {
    setCertifying(true);
    setCertifyError('');

    try {
      const aiDraftContent = originalContent || transcription.content;
      const hasAiDraftVersion = versions.some((v) => v.change_type === 'ai_draft');

      if (!hasAiDraftVersion && aiDraftContent) {
      try {
        await createNewVersion(
          aiDraftContent,
          'ai_draft',
          'Original AI-generated draft preserved at certification'
        );
      } catch {
        // Version history is optional; certification should still proceed.
      }
      }

      try {
        await transcriptionAPI.certifyTranscription(id, {
          correction_summary: correctionSummary,
          certified_by_name: userName,
          certifying_role: displayRole || role,
        });
      } catch (apiErr) {
        const status = apiErr.response?.status;
        if (status !== 404 && status !== 405 && status !== 501) {
          throw apiErr;
        }
      }

      setTranscription((prev) => ({
        ...prev,
        transcript_status: TRANSCRIPT_STATUS.CERTIFIED,
        certified_at: new Date().toISOString(),
        certified_by: userName,
        certifying_role: displayRole || role,
      }));
      setShowCertifyModal(false);
      setIsEditing(false);
      setIsEditingSummary(false);
    } catch (err) {
        setCertifyError(err.response?.data?.error || err.response?.data?.message || err.message || 'Certification failed.');
    } finally {
      setCertifying(false);
    }
  };

  useEffect(() => {
    if (!showRegenerateModal || !transcription.audio_asset_id) {
      return undefined;
    }
    let cancelled = false;
    transcriptionAPI
      .getAvailableLanguages(transcription.audio_asset_id)
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data?.data || resp.data || {};
        setTargetLanguages(data.available_languages || []);
      })
      .catch(() => {
        if (!cancelled) setTargetLanguages(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showRegenerateModal, transcription.audio_asset_id]);

  const handleRegenerate = async ({ target_language, reason_code }) => {
    setRegenSubmitting(true);
    setRegenError('');

    try {
      const resp = await transcriptionAPI.requestRegeneration(id, {
        target_language,
        reason_code,
        requested_by: userName,
      });
      const result = resp.data?.data || resp.data;

      if (result?.status === 'pending_approval') {
        setRegenError('');
        setShowRegenerateModal(false);
        toast.success('Regeneration submitted for admin approval.');
        return;
      }

      const refreshResponse = await transcriptionAPI.getTranscription(id);
      const refreshData = refreshResponse.data?.data || refreshResponse.data;
      if (refreshData) {
        applyParsedTranscription(refreshData);
      }

      setShowRegenerateModal(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setRegenError('The original recording has been deleted. Regeneration is not available.');
      } else {
        setRegenError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            'Failed to queue regeneration.'
        );
      }
    } finally {
      setRegenSubmitting(false);
    }
  };

  // Poll while this transcript or any linked language version is still processing
  useEffect(() => {
    if (!id) return undefined;
    const shouldPoll = isJobProcessing || hasPendingLanguageVersions(languageVersions);
    if (!shouldPoll) return undefined;

    const poll = async () => {
      try {
        const response = await transcriptionAPI.getTranscription(id);
        const data = response.data?.data || response.data;
        if (!data) return;

        const parsed = parseTranscriptionRecord(data);
        setLanguageVersions(parsed.language_versions || []);

        const currentVersion = (parsed.language_versions || []).find(
          (v) => String(v.id) === String(id)
        );
        if (currentVersion && currentVersion.status !== transcription.status) {
          applyParsedTranscription(data);
        } else if (!transcription.content && (data.transcribed_content || data.content || data.text)) {
          applyParsedTranscription(data);
        }
        setSummary(extractSummaryFromRecord(data));
      } catch {
        // Ignore transient poll errors
      }
    };

    const intervalId = setInterval(poll, 3000);
    return () => clearInterval(intervalId);
  }, [id, languageVersions, transcription.status, transcription.content, isJobProcessing]);

  // Fetch transcription data from API
  useEffect(() => {
    const fetchTranscription = async () => {
      try {
        setLoading(true);
        // console.log('Fetching transcription with ID:', id);
        const response = await transcriptionAPI.getTranscription(id);
        
        // console.log('Transcription API response:', response);
        // console.log('Transcription data:', response.data);
        
        if (response.data) {
          const data = response.data.data || response.data;
          // console.log('Processing transcription data:', data);
          
          // Log ALL fields to see what's available
          // console.log('Full TranscriptionDetail data for duration analysis:', {
          //   id: data.id,
          //   title: data.title || data.filename,
          //   allFields: Object.keys(data),
          //   allValues: data
          // });
          
          // Log summary-related fields specifically
          // console.log('Summary-related fields in API response:', {
          //   summary: data.summary,
          //   transcription_summary: data.transcription_summary,
          //   legal_summary: data.legal_summary,
          //   summary_text: data.summary_text,
          //   ai_summary: data.ai_summary,
          //   content_summary: data.content_summary,
          //   summary_fields: Object.keys(data).filter(key => key.toLowerCase().includes('summary')),
          //   all_fields_containing_summary: Object.keys(data).filter(key => 
          //     key.toLowerCase().includes('summary') || 
          //     key.toLowerCase().includes('analysis') || 
          //     key.toLowerCase().includes('outcome') ||
          //     key.toLowerCase().includes('conclusion') ||
          //     key.toLowerCase().includes('legal') ||
          //     key.toLowerCase().includes('ai')
          //   ),
          //   all_data_keys: Object.keys(data),
          //   data_sample: Object.keys(data).reduce((acc, key) => {
          //     const value = data[key];
          //     if (typeof value === 'string' && value.length > 50) {
          //       acc[key] = value.substring(0, 100) + '...';
          //     } else {
          //       acc[key] = value;
          //     }
          //     return acc;
          //   }, {})
          // });
          
          const rawDuration = getRawDurationFromData(data);
          const parsedDuration = parseDurationToSeconds(rawDuration);
          const transcriptionData = applyParsedTranscription(data, parsedDuration);
          const resolvedId = data.id || data.transcription_id;
          if (resolvedId && String(resolvedId) !== String(id)) {
            const detailBase = window.location.pathname.startsWith('/admin/')
              ? '/admin/transcriptions'
              : '/transcriptions';
            navigate(`${detailBase}/${resolvedId}`, { replace: true });
          }
          
          // If content is still empty but we have a title/filename, try fallback search by key
          if (!transcriptionData.content && (data.title || data.filename || data.audio_filename)) {
            try {
              const key = data.title || data.filename || data.audio_filename;
              // console.log('Primary content empty, attempting fallback fetch with key:', key);
              const altResp = await transcriptionAPI.getTranscription(key);
              const alt = altResp.data?.data || altResp.data;
              if (alt && (alt.transcribed_content || alt.content || alt.transcription || alt.text)) {
                transcriptionData.content = alt.transcribed_content || alt.content || alt.transcription || alt.text;
                transcriptionData.wordCount = calculateWordCount(transcriptionData.content);
                // console.log('Fallback content found via key search.');
              }
            } catch (fallbackErr) {
              // console.warn('Fallback key search did not find content:', fallbackErr);
            }
          }
          
          setSummary(extractSummaryFromRecord(data));
        } else {
          throw new Error('No data received from API');
        }
      } catch (error) {
        // console.error('Error fetching transcription:', error);
        // console.error('Error details:', error.response?.data);
        // console.error('Error status:', error.response?.status);
        // console.error('Error message:', error.message);
        // console.error('Full error object:', error);
        
        // Check if it's a 404 (transcription not found) vs other errors
        if (error.response?.status === 404) {
          setError('Transcription not found');
        } else if (error.response?.status === 410) {
          setError('Original session recording has been deleted (410). Derived transcripts remain for reference.');
        } else if (error.response?.status === 405) {
          setError('Method not allowed - API endpoint issue');
        } else {
          setError(`Failed to load transcription: ${error.response?.data?.message || error.message}`);
        }
        
        // Set empty transcription data on error - no fallback
        const emptyData = {
          id: id,
          title: '',
          content: '',
          date: '',
          duration: '',
          status: '',
          wordCount: 0,
          language: 'English'
        };
        
        setTranscription(emptyData);
        setEditedContent('');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTranscription();
      fetchVersions();
      fetchSummaryVersions();
    }
  }, [id]);

  // Update word count dynamically as content is edited
  useEffect(() => {
    if (isEditing) {
      const wordCount = calculateWordCount(editedContent);
      setTranscription(prev => ({ ...prev, wordCount }));
    }
  }, [editedContent, isEditing]);

  const handleSave = async () => {
    if (isLocked) {
      setError('Certified transcripts cannot be edited.');
      return;
    }
    try {
      setLoading(true);
      // console.log('Saving transcription with content:', editedContent);
      // console.log('Content type:', typeof editedContent);
      // console.log('Content length:', editedContent ? editedContent.length : 0);
      
      // Check if content has actually changed
      const contentChanged = hasContentChanged(originalContent, editedContent);
      
      if (!contentChanged) {
        // console.log('No changes detected, skipping version creation');
        // Just exit editing mode without creating a new version
        setIsEditing(false);
        setShowOriginal(false);
        setError(null);
        return;
      }
      
      // Since we're using Markdown, we can calculate word count directly
      const wordCount = calculateWordCount(editedContent);

      // Create a new version with the Markdown content preserved
      const changeSummary = `Edited by ${userName} - ${wordCount} words`;
      await transcriptionAPI.updateTranscription(id, { content: editedContent });
      await createNewVersion(editedContent, 'edit', changeSummary);
      
      // Update local state with Markdown content
    setTranscription({ ...transcription, content: editedContent, wordCount });
      // console.log('Transcription state updated with:', editedContent);
      
      // Clear edited content since it's now saved
      setEditedContent('');
    setIsEditing(false);
    setShowOriginal(false); // Reset original view indicator
      
      // Clear any previous errors
      setError(null);
    } catch (error) {
      // console.error('Error saving transcription:', error);
      setError(`Failed to save transcription: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedContent('');
    setIsEditing(false);
  };

  const handleRenameSpeaker = (speakerLabel, currentName) => {
    if (isLocked) return;
    setSpeakerLabelError('');
    setRenameTarget({ speakerLabel, currentName });
  };

  const handleSaveSpeakerLabel = async ({ speakerLabel, newName, applyToCase }) => {
    setSavingSpeakerLabel(true);
    setSpeakerLabelError('');
    try {
      await transcriptionAPI.updateSpeakerLabels(id, {
        labels: { [speakerLabel]: newName },
        apply_to_case: applyToCase,
        case_reference: transcription.case_reference,
      });

      setSpeakerLabelMap((prev) => ({ ...prev, [speakerLabel]: newName }));
      setSpeakerSegments((prev) =>
        prev.map((seg) =>
          seg.speaker_label === speakerLabel ? { ...seg, role_name: newName } : seg
        )
      );
      setTranscription((prev) => ({
        ...prev,
        segments: (prev.segments || []).map((seg) =>
          seg.speaker_label === speakerLabel ? { ...seg, role_name: newName } : seg
        ),
      }));
      setRenameTarget(null);
    } catch (err) {
      setSpeakerLabelError(
        err.response?.data?.message || err.message || 'Failed to save speaker label.'
      );
    } finally {
      setSavingSpeakerLabel(false);
    }
  };

  const handleEditSummary = () => {
    // console.log('handleEditSummary called');
    // console.log('Current summary state:', summary);
    // console.log('Type of summary:', typeof summary);
    const formattedSummary = formatSummary(summary);
    // console.log('Formatted summary:', formattedSummary);
    // console.log('Final value being set to editedSummary:', formattedSummary || '');

    setEditedSummary(formattedSummary || '');
    setIsEditingSummary(true);
    setIsEditDropdownOpen(false);

    // Scroll to the summary section
    const summarySection = document.getElementById('summary-section');
    if (summarySection) {
      summarySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSaveSummary = async () => {
    if (isLocked) {
      setError('Certified transcripts cannot be edited.');
      return;
    }
    try {
      setLoading(true);
      // console.log('Saving summary with content:', editedSummary);
      // console.log('Content type:', typeof editedSummary);
      // console.log('Content length:', editedSummary ? editedSummary.length : 0);

      // Check if summary content has actually changed
      const originalSummary = formatSummary(summary);
      const summaryChanged = hasContentChanged(originalSummary, editedSummary);
      
      if (!summaryChanged) {
        // console.log('No changes detected in summary, skipping version creation');
        // Just exit editing mode without creating a new version
        setIsEditingSummary(false);
        setError(null);
        return;
      }

      // Create a new summary version with rich HTML content preserved
      await createNewSummaryVersion(editedSummary, 'edit', `Edited by ${userName}`);

      // Update local state with rich HTML content
      setSummary(editedSummary);
      // console.log('Summary state updated with:', editedSummary);
      
      // Clear edited summary since it's now saved
      setEditedSummary('');
      setIsEditingSummary(false);
    } catch (error) {
      // console.error('Error saving summary:', error);
      setError(`Failed to save summary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create new summary version
  const createNewSummaryVersion = async (content, changeType = 'edit', changeSummary = '') => {
    try {
      setSavingSummaryVersion(true);
      const response = await fetch(`${VERSION_CONTROL_API_URL}/transcriptions/original/${id}/summary-versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content,
          change_type: changeType,
          change_summary: changeSummary,
          created_by_name: userName,
          title: transcription.title
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh summary versions list
        await fetchSummaryVersions();
        return data.data;
      } else {
        throw new Error(data.error || 'Failed to create summary version');
      }
    } catch (error) {
      // console.error('Error creating summary version:', error);
      throw error;
    } finally {
      setSavingSummaryVersion(false);
    }
  };

  const handleCancelSummaryEdit = () => {
    setEditedSummary('');
    setIsEditingSummary(false);
  };

  const handleDownloadAsDocx = () => {
    const contentToDownload = prependDraftNotice(
      isEditing ? editedContent : transcription.content,
      transcription
    );
    try {
      downloadAsDocx(contentToDownload, transcription);
    } catch (error) {
      console.error('DOCX download failed:', error);
      alert('Could not export the transcript as DOCX.');
    }
  };

  const handleDownloadAsPdf = async () => {
    try {
      // Redesigned, letterhead-styled conversation transcript (speaker + timestamp
      // per utterance). Falls back to the plain text export when there is nothing
      // to template.
      const model = buildCourtTranscriptDocument(
        transcription,
        speakerSegments,
        speakerLabelMap,
        { courtName: user?.court_name || '' }
      );
      if (model) {
        const fileName = `${String(transcription?.title || 'transcription')
          .replace(/[^a-z0-9]+/gi, '_')
          .replace(/^_+|_+$/g, '')
          .toLowerCase() || 'transcription'}_transcript.pdf`;
        await downloadCourtTranscriptPdf(model, fileName);
        return;
      }
      const contentToDownload = prependDraftNotice(
        isEditing ? editedContent : transcription.content,
        transcription
      );
      await downloadAsPdf(contentToDownload, transcription);
    } catch (error) {
      console.error('PDF download failed:', error);
      alert('Could not export the transcript as PDF.');
    }
  };

  const handleDownloadSummaryDocx = () => {
    try {
      downloadSummaryDocx(summary, transcription, isEditingSummary, editedSummary);
    } catch (error) {
      console.error('Summary DOCX download failed:', error);
      alert('Could not export the summary as DOCX.');
    }
  };

  const handleDownloadSummaryPdf = async () => {
    try {
      await downloadSummaryPdf(summary, transcription, isEditingSummary, editedSummary);
    } catch (error) {
      console.error('Summary PDF download failed:', error);
      alert('Could not export the summary as PDF.');
    }
  };

  const handleDownloadVersionAsPDF = (version) => {
    try {
      downloadVersionAsPDF(version, transcription);
    } catch (error) {
      console.error('Version PDF download failed:', error);
      alert('Could not export this version as PDF.');
    }
  };

  const handleDownloadVersionAsDOCX = (version) => {
    try {
      downloadVersionAsDOCX(version, transcription);
    } catch (error) {
      console.error('Version DOCX download failed:', error);
      alert('Could not export this version as DOCX.');
    }
  };

  const handleDownloadSummaryVersionAsPDF = (version) => {
    try {
      downloadSummaryVersionAsPDF(version, transcription);
    } catch (error) {
      console.error('Summary version PDF download failed:', error);
      alert('Could not export this summary version as PDF.');
    }
  };

  const handleDownloadSummaryVersionAsDOCX = (version) => {
    try {
      downloadSummaryVersionAsDOCX(version, transcription);
    } catch (error) {
      console.error('Summary version DOCX download failed:', error);
      alert('Could not export this summary version as DOCX.');
    }
  };

  const editVersion = (version) => {
    if (isLocked) return;
    // Capture the original content before editing starts
    setOriginalContent(version.content);
    // Set the edited content to the version content and enable editing
    setEditedContent(version.content);
    setIsEditing(true);
    setShowOriginal(false); // Not showing original when editing a version
    
    // Scroll to the editing area
    const editingArea = document.getElementById('editing-area');
    if (editingArea) {
      editingArea.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const viewOriginalTranscript = () => {
    if (isCertified) {
      const aiDraft =
        versions.find((v) => v.change_type === 'ai_draft') ||
        versions.reduce((min, v) => {
          if (!min || v.version_number < min.version_number) return v;
          return min;
        }, null);
      if (aiDraft) {
        setSelectedVersion(aiDraft);
        return;
      }
    }
    // Capture the original content before editing starts
    setOriginalContent(originalContent);
    // Set the edited content to the original transcription content and enable editing
    setEditedContent(originalContent);
    setIsEditing(true);
    setShowOriginal(true); // Show original indicator
    
    // Scroll to the editing area
    const editingArea = document.getElementById('editing-area');
    if (editingArea) {
      editingArea.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const viewOriginalSummary = () => {
    // Set the edited summary to the current summary and enable editing
    setEditedSummary(formatSummary(summary) || '');
    setIsEditingSummary(true);

    // Scroll to the summary section
    const summarySection = document.getElementById('summary-section');
    if (summarySection) {
      summarySection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Close dropdown when clicking outside and handle mobile responsiveness
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsEditDropdownOpen(false);
      }
      if (isDownloadDropdownOpen && !event.target.closest('.download-dropdown')) {
        setIsDownloadDropdownOpen(false);
      }
      if (isVersionsDropdownOpen && !event.target.closest('.versions-dropdown')) {
        setIsVersionsDropdownOpen(false);
      }
    };

    const handleResize = () => {
      // Close dropdowns on screen resize to prevent positioning issues
      setIsEditDropdownOpen(false);
      setIsDownloadDropdownOpen(false);
      setIsVersionsDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [isEditDropdownOpen, isDownloadDropdownOpen, isVersionsDropdownOpen]);

  // Show loading state
  if (loading && !transcription.content) {
    return <PageLoadingState message="Loading transcription…" />;
  }

  return (
    <>
        {/* Header */}
        <TranscriptionHeader
          transcription={transcription}
          error={error}
          userName={userName}
          isEditDropdownOpen={isEditDropdownOpen}
          setIsEditDropdownOpen={setIsEditDropdownOpen}
          isDownloadDropdownOpen={isDownloadDropdownOpen}
          setIsDownloadDropdownOpen={setIsDownloadDropdownOpen}
          isVersionsDropdownOpen={isVersionsDropdownOpen}
          setIsVersionsDropdownOpen={setIsVersionsDropdownOpen}
          showVersions={showVersions}
          showSummaryVersions={showSummaryVersions}
          setShowVersions={setShowVersions}
          setShowSummaryVersions={setShowSummaryVersions}
          versions={versions}
          summaryVersions={summaryVersions}
          summary={summary}
          isEditing={isEditing}
          isEditingSummary={isEditingSummary}
          loading={loading}
          isLocked={isLocked}
          canCertify={canCertify}
          canRequestRegeneration={canRequestRegeneration}
          showRegenerateButton={showRegenerateButton}
          onGenerateLanguage={() => setShowRegenerateModal(true)}
          onCertify={() => setShowCertifyModal(true)}
          onEditTranscripts={() => {
            if (isLocked) return;
            setOriginalContent(transcription.content);
            setEditedContent(transcription.content);
            setIsEditing(true);
          }}
          onEditSummary={() => {
                              setEditedSummary(formatSummary(summary) || '');
                              setIsEditingSummary(true);
                              const summarySection = document.getElementById('summary-section');
                              if (summarySection) {
                                summarySection.scrollIntoView({ behavior: 'smooth' });
                              }
                            }}
          onDownloadDocx={handleDownloadAsDocx}
          onDownloadPdf={handleDownloadAsPdf}
          onDownloadSummaryDocx={handleDownloadSummaryDocx}
          onDownloadSummaryPdf={handleDownloadSummaryPdf}
        />

        <JobMetadataPanel
          transcription={transcription}
          onRetrySuccess={() => window.location.reload()}
          onStatusChange={(status) =>
            setTranscription((prev) => ({ ...prev, status, jobStatus: status }))
          }
        />

        <DraftBanner transcription={transcription} />

        {isRestrictedLanguage && (
          <div className="md-alert md-alert-warning mb-4">
            <strong>Restricted — Draft Only:</strong> This language is below the platform accuracy threshold.
            Exports and certification require admin review.
          </div>
        )}

        <LanguageVersionTabs
          versions={languageVersions}
          currentId={transcription.id || id}
        />

        <RegenerationAuditPanel transcription={transcription} />

        <AudioAssetPanel
          audioAsset={transcription.audio_asset}
          audioAssetId={transcription.audio_asset_id}
          durationSeconds={transcription.duration_seconds}
          languageVersionCount={languageVersions.length}
          sessionDeleted={transcription.session_deleted}
        />

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 mb-6 items-stretch">
          {/* Transcript panel is disabled for now; audio review takes the full row. */}
          <div id="transcription-content-section" className="xl:col-span-12 min-w-0">
            <AudioReviewPanel
              className="mb-0 h-full"
              audioUrl={transcription.audio_url}
              audioRef={audioSync.audioRef}
              currentTime={audioSync.currentTime}
              isPlaying={audioSync.isPlaying}
              onTimeUpdate={audioSync.handleTimeUpdate}
              onTogglePlay={audioSync.togglePlay}
              durationSeconds={transcription.duration_seconds}
              onEnded={() => audioSync.setIsPlaying(false)}
            />
          </div>
          {/* <div className="xl:col-span-8 min-w-0">
            <TranscriptionContent
              transcription={transcription}
              error={error}
              isEditing={isEditing}
              editedContent={editedContent}
              setEditedContent={setEditedContent}
              handleSave={handleSave}
              handleCancel={handleCancel}
              loading={loading}
              isLocked={isLocked}
              showDraftWatermark={!isCertified}
              speakerSegments={speakerSegments}
              speakerLabelMap={speakerLabelMap}
              activeSegmentIndex={audioSync.activeSegmentIndex}
              onSegmentClick={audioSync.seekToSegment}
              onRenameSpeaker={handleRenameSpeaker}
              isProcessing={isJobProcessing}
            />
          </div> */}
        </div>

        {/* Version History */}
        <div id="version-history-section">
          <VersionHistory
            showVersions={showVersions}
            versions={versions}
            versionsLoading={versionsLoading}
            selectedVersion={selectedVersion}
            setSelectedVersion={setSelectedVersion}
            downloadVersionAsPDF={handleDownloadVersionAsPDF}
            downloadVersionAsDOCX={handleDownloadVersionAsDOCX}
            editVersion={editVersion}
            restoreVersion={restoreVersion}
            savingVersion={savingVersion}
            viewOriginalTranscript={viewOriginalTranscript}
            originalContent={originalContent}
            loading={loading}
            error={error}
            isLocked={isLocked}
            isCertified={isCertified}
            aiDraftVersionNumber={aiDraftVersionNumber}
          />
        </div>


        {/* Modals */}
        <SummaryVersionModal
          selectedSummaryVersion={selectedSummaryVersion}
          setSelectedSummaryVersion={setSelectedSummaryVersion}
          savingSummaryVersion={savingSummaryVersion}
          createNewSummaryVersion={createNewSummaryVersion}
          userName={userName}
          transcription={transcription}
          processVersionContent={processVersionContent}
        />

        <VersionModal
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          savingVersion={savingVersion}
          restoreVersion={restoreVersion}
          userName={userName}
          transcription={transcription}
          processVersionContent={processVersionContent}
          isLocked={isLocked}
        />

        <CertifyModal
          open={showCertifyModal}
          onClose={() => {
            setShowCertifyModal(false);
            setCertifyError('');
          }}
          onConfirm={handleCertify}
          transcriptionTitle={transcription.title}
          certifying={certifying}
          error={certifyError}
        />

        <SpeakerRenameDialog
          open={Boolean(renameTarget)}
          speakerLabel={renameTarget?.speakerLabel}
          currentName={renameTarget?.currentName}
          onClose={() => {
            setRenameTarget(null);
            setSpeakerLabelError('');
          }}
          onSave={handleSaveSpeakerLabel}
          saving={savingSpeakerLabel}
          error={speakerLabelError}
          caseReference={transcription.case_reference}
        />

        <RegenerateModal
          open={showRegenerateModal}
          onClose={() => {
            setShowRegenerateModal(false);
            setRegenError('');
          }}
          onSubmit={handleRegenerate}
          submitting={regenSubmitting}
          error={regenError}
          existingLanguages={Array.from(existingLanguageLabels)}
          sourceLanguage={getLanguageLabel(transcription.language)}
          languages={targetLanguages}
        />

        {/* Summary Versions */}
        <div id="summary-version-history-section">
          <SummaryVersionHistory
            showSummaryVersions={showSummaryVersions}
            summary={summary}
            summaryVersions={summaryVersions}
            summaryVersionsLoading={summaryVersionsLoading}
            selectedSummaryVersion={selectedSummaryVersion}
            setSelectedSummaryVersion={setSelectedSummaryVersion}
            downloadSummaryVersionAsPDF={handleDownloadSummaryVersionAsPDF}
            downloadSummaryVersionAsDOCX={handleDownloadSummaryVersionAsDOCX}
            setEditedSummary={setEditedSummary}
            setIsEditingSummary={setIsEditingSummary}
            setIsEditDropdownOpen={setIsEditDropdownOpen}
            createNewSummaryVersion={createNewSummaryVersion}
            savingSummaryVersion={savingSummaryVersion}
            viewOriginalSummary={viewOriginalSummary}
            loading={loading}
            error={error}
            userName={userName}
          />
        </div>

        {/* Summary Section */}
        <SummarySection
          transcription={transcription}
          summary={summary}
          isEditingSummary={isEditingSummary}
          editedSummary={editedSummary}
          setEditedSummary={setEditedSummary}
          handleSaveSummary={handleSaveSummary}
          handleCancelSummaryEdit={handleCancelSummaryEdit}
          loading={loading}
          isProcessing={isJobProcessing}
        />
    </>
  );
};

export default TranscriptionDetail;